import { useMemo } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useWatch } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import {
  BaseForm,
  FormInput,
  FormSelect,
  FormTag,
  FormTextarea,
} from "components/BaseForm";
import ACL_RELATIONSHIPS from "acl-relationships";
import EmailHtmlFrame from "components/email/EmailHtmlFrame";
import tagConstants from "constants/Tag";
import { decodeHtml } from "helpers/emailHelper";
import useAuthorized from "hooks/useAuthorized";
import { invalidateEmailSignatureTableQuery } from "hooks/useEmailSignatureTable";
import usePageTitle from "hooks/usePageTitle";
import EmailFooterService from "services/EmailFooters";
import EmailSignatureService from "services/EmailSignatures";
import EmailTemplateService from "services/EmailTemplate";

const MODEL_TYPE = tagConstants.taggableModels.email_signature;

const schema = z.object({
  name: z.string().min(1, "Name is required"),
  html: z.string().min(1, "Custom HTML is required"),
  template_id: z.number().nullable(),
  footer_id: z.number().nullable(),
  tags: z.array(z.any()),
});

const mapForSelect = records =>
  records.map(({ id, name }) => ({ key: id, value: id, text: name }));

const getTemplateOptions = async () => {
  const templates = await EmailTemplateService.getEmailTemplatesForFilters();
  return mapForSelect(templates);
};

const getFooterOptions = async () => {
  const footers = await EmailFooterService.getEmailFootersForFilters();
  return mapForSelect(footers);
};

/**
 * Renders the signature HTML inside the chosen template and above the chosen
 * footer, so the user sees it framed the way a recipient would. `template_id`
 * and `footer_id` drive the frame only and are never saved with the signature.
 *
 * The preview goes in an iframe because email templates carry their own
 * `<style>` blocks. Rendered into the application document those rules are
 * global and restyle the app around the preview.
 */
const SignaturePreview = () => {
  const templateId = useWatch({ name: "template_id" });
  const footerId = useWatch({ name: "footer_id" });
  const html = useWatch({ name: "html" });

  const { data: templateHtml = "" } = useQuery({
    queryKey: ["email-template-html", templateId],
    queryFn: async () => {
      const template = await EmailTemplateService.getEmailTemplate(templateId);
      return decodeHtml(template.html);
    },
    enabled: !!templateId,
  });

  const { data: footerHtml = "" } = useQuery({
    queryKey: ["email-footer-html", footerId],
    queryFn: async () => {
      const footer = await EmailFooterService.getEmailFooter(footerId);
      return decodeHtml(footer.html);
    },
    enabled: !!footerId,
  });

  const previewHtml = useMemo(() => {
    const before = templateHtml.indexOf("</body>");
    return (
      templateHtml.substring(0, before) +
      (html ?? "") +
      footerHtml +
      templateHtml.substring(before)
    );
  }, [templateHtml, footerHtml, html]);

  return (
    <EmailHtmlFrame
      html={previewHtml}
      title="Email signature preview"
      autoHeight
      style={{ minHeight: 120 }}
    />
  );
};

const EmailSignature = ({ mode }) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isEdit = mode === "edit";
  const cloneId = searchParams.get("clone");
  const isClone = !isEdit && !!cloneId;
  const sourceId = isEdit ? id : cloneId;

  const canWrite = useAuthorized([
    isEdit
      ? ACL_RELATIONSHIPS.emailSignatures.edit
      : ACL_RELATIONSHIPS.emailSignatures.create,
  ]);

  const {
    data: signatureData,
    isSuccess: signatureLoaded,
    isError: signatureError,
    isFetchedAfterMount,
  } = useQuery({
    queryKey: ["email-signature", sourceId],
    queryFn: () => EmailSignatureService.getEmailSignature(sourceId),
    enabled: !!sourceId,
    retry: false,
  });

  const { data: templateOptions = [] } = useQuery({
    queryKey: ["email-template-options"],
    queryFn: getTemplateOptions,
  });

  const { data: footerOptions = [] } = useQuery({
    queryKey: ["email-footer-options"],
    queryFn: getFooterOptions,
  });

  const defaultValues = useMemo(
    () => ({
      // A clone starts unnamed so the user is forced to pick a distinct name.
      name: isClone ? "" : (signatureData?.name ?? ""),
      html: signatureData?.html ?? "",
      template_id: null,
      footer_id: null,
      tags: signatureData?.tags ?? [],
    }),
    [signatureData, isClone]
  );

  const pageTitle = isEdit
    ? "Edit Email Signature"
    : isClone
      ? "Clone Email Signature"
      : "New Email Signature";

  usePageTitle(isEdit ? signatureData?.name : pageTitle);

  const handleSubmit = async ({ name, html, tags }) => {
    if (isEdit) {
      await EmailSignatureService.editEmailSignature(id, name, html, tags);
    } else {
      await EmailSignatureService.createEmailSignature(name, html, tags);
    }
  };

  const handleSuccess = async () => {
    await invalidateEmailSignatureTableQuery();
    toast.success(
      isEdit ? "Email signature updated" : "Email signature created"
    );
    navigate("/settings/email_signatures");
  };

  const handleCancel = () => navigate("/settings/email_signatures");

  if (!canWrite) {
    return <Navigate to="/settings/email_signatures" replace />;
  }

  if (signatureError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <p>{"Couldn't load this email signature. Please try again."}</p>
        <Link to="/settings/email_signatures">Back to Email Signatures</Link>
      </div>
    );
  }

  // isFetchedAfterMount, not just isSuccess: a cached entry resolves
  // synchronously as successful, and react-hook-form latches defaultValues at
  // the form's first render. Mounting on that payload would strand the form on
  // pre-save values, and saving would write them back over a newer edit.
  if (sourceId && !(signatureLoaded && isFetchedAfterMount)) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-(--color-text-muted)">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-center tw:font-sans tw:text-(--color-text-primary)">
      <div className="tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        <BaseForm
          enablePageTitle
          pageTitle={pageTitle}
          backLink={{
            to: "/settings/email_signatures",
            label: "Back to Email Signatures",
          }}
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
          submitButtonText="Save"
        >
          <Card>
            <CardHeader>
              <CardTitle>Signature</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="tw:flex tw:flex-col tw:gap-5">
                <FormInput name="name" label="Name" required />
                <FormTextarea
                  name="html"
                  label="Custom HTML"
                  required
                  rows={6}
                />
                <FormTag name="tags" modelType={MODEL_TYPE} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="tw:flex tw:flex-col tw:gap-5">
                <FormSelect
                  name="template_id"
                  label="Choose a Preview Template"
                  options={templateOptions}
                  clearable
                />
                <FormSelect
                  name="footer_id"
                  label="Choose a Preview Footer"
                  options={footerOptions}
                  clearable
                />
                <SignaturePreview />
              </div>
            </CardContent>
          </Card>
        </BaseForm>
      </div>
    </div>
  );
};

export default EmailSignature;
